"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { translations, type Language } from "./translations"

const STORAGE_KEY = "lemanit-language"

function detectBrowserLanguage(): Language {
  if (typeof navigator === "undefined") return "en"
  const rawLang = navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage || "en"
  const langCode = rawLang.toLowerCase().split("-")[0]
  if (langCode === "fr") return "fr"
  return "en"
}

function detectUrlLanguage(): Language | null {
  if (typeof window === "undefined") return null
  const pathLang = window.location.pathname.split("/")[1]
  if (pathLang === "fr" || pathLang === "en") return pathLang
  return null
}

interface LanguageContextValue {
  lang: Language
  setLang: (lang: Language) => void
  t: (section: string, key: string, subKey?: string, nestedKey?: string) => string
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>("en")

  // Initialize from URL > localStorage > browser on mount
  useEffect(() => {
    let initial: Language = "en"
    try {
      const urlLang = detectUrlLanguage()
      const stored = localStorage.getItem(STORAGE_KEY) as Language | null
      if (urlLang) {
        initial = urlLang
      } else if (stored === "fr" || stored === "en") {
        initial = stored
      } else {
        initial = detectBrowserLanguage()
      }
    } catch {
      initial = detectBrowserLanguage()
    }
    setLangState(initial)
    document.documentElement.lang = initial
  }, [])

  const setLang = (newLang: Language) => {
    setLangState(newLang)
    try {
      localStorage.setItem(STORAGE_KEY, newLang)
    } catch {
      // ignore
    }
    document.documentElement.lang = newLang
  }

  const t = (section: string, key: string, subKey?: string, nestedKey?: string): string => {
    const dict = translations[lang] as Record<string, unknown>
    const sec = dict[section] as Record<string, unknown> | undefined
    if (!sec) return key

    if (nestedKey && subKey) {
      const group = sec[key] as Record<string, unknown> | undefined
      if (!group) return nestedKey
      const item = group[subKey] as Record<string, unknown> | undefined
      if (!item) return nestedKey
      const val = item[nestedKey]
      return typeof val === "string" ? val : nestedKey
    }

    if (subKey) {
      const item = sec[key] as Record<string, unknown> | undefined
      if (!item) return subKey
      const val = item[subKey]
      return typeof val === "string" ? val : subKey
    }

    const val = sec[key]
    return typeof val === "string" ? val : key
  }

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return ctx
}
